import { useTranslation } from 'react-i18next'

/**
 * Renders one legal document from the `legal` namespace.
 *
 * The text lives in the locale files rather than in the component so the same
 * document can be shown in the agreement modal and on its own page without
 * being written twice, and so it translates with the rest of the console.
 */

export type LegalDocumentId = 'terms' | 'privacy' | 'cookies'

interface LegalSection {
  heading: string
  body?: string
  items?: string[]
}

interface LegalDocumentProps {
  document: LegalDocumentId
  /** Set on the page, cleared in the modal, which draws its own heading. */
  showTitle?: boolean
}

export default function LegalDocument({ document, showTitle = true }: LegalDocumentProps) {
  const { t } = useTranslation('legal')

  // returnObjects is how i18next hands back the section array; a locale file
  // that failed to load would otherwise return the key string and break .map.
  const sections = t(`${document}.sections`, { returnObjects: true }) as
    | LegalSection[]
    | string

  const safeSections: LegalSection[] = Array.isArray(sections) ? sections : []

  return (
    <div className="prose prose-sm max-w-none">
      {showTitle && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t(`${document}.title`)}</h3>
      )}

      <div className="space-y-4 text-gray-700">
        {safeSections.map((section, index) => (
          <section key={`${document}-${index}`}>
            <h4 className="font-semibold text-gray-900 mb-2">{section.heading}</h4>
            {section.body && <p>{section.body}</p>}
            {section.items && (
              <ul className="list-disc list-inside space-y-1 ml-2">
                {section.items.map((item, itemIndex) => (
                  <li key={`${document}-${index}-${itemIndex}`}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  )
}
